import { Navigate, Route, useParams } from 'react-router-dom';

/**
 * Legacy → canonical route redirects.
 *
 * The route scheme is `/<group>/<page>` (driven by NAV_GROUPS in
 * `types/navigation.ts`). These entries keep old bookmarked / shared URLs working
 * by forwarding them — and any sub-path — to the new canonical paths.
 *
 * This file is intentionally STANDALONE and decoupled from the app's real routing:
 * the canonical routes don't know it exists. To retire the old URLs later, delete
 * this file and the single `{legacyRedirectRoutes()}` line in App.tsx — nothing
 * else references these paths.
 */
export const LEGACY_ROUTE_REDIRECTS: ReadonlyArray<{ from: string; to: string }> = [
  // Organization group
  { from: '/admin-traders', to: '/organization/org-settings' },
  { from: '/manager-traders', to: '/organization/org-settings' },
  { from: '/manager-org-account', to: '/organization/org-account' },
  { from: '/exports', to: '/organization/exports' },
  // Administration group
  { from: '/admin-manage', to: '/administration/management' },
  { from: '/admin-g1-health', to: '/administration/g1-health' },
  // Research group
  { from: '/g3', to: '/research/g3' },
  { from: '/g4', to: '/research/g4' },
  { from: '/streamrift', to: '/research/streamrift' },
  // Bots group
  { from: '/g9', to: '/bots/g9' },
  { from: '/recycler', to: '/bots/recycler' },
];

/** Forwards `<from>` and any sub-path `<from>/<rest>` → `<to>[/<rest>]`, so deep
 *  links like `/admin-traders/trader/42` land on `/organization/org-settings/trader/42`. */
function LegacyRedirect({ to }: { to: string }) {
  const rest = useParams()['*'];
  return <Navigate to={rest ? `${to}/${rest}` : to} replace />;
}

/** The redirect `<Route>`s to spread inside the app's `<Routes>`. Paths are
 *  relative (no leading slash) because they mount inside the authed shell's
 *  `<Routes>`, which is itself based at `/`. */
export function legacyRedirectRoutes() {
  return LEGACY_ROUTE_REDIRECTS.map(({ from, to }) => (
    <Route key={from} path={`${from.replace(/^\//, '')}/*`} element={<LegacyRedirect to={to} />} />
  ));
}
